import { useState, useEffect, useCallback } from 'react';
import type { CatalogProduct } from '@techgitter/contracts';
import {
  getProducts,
  invalidateProductsCache,
} from '../services/productCacheService';

let cachedProductMap: Map<string, CatalogProduct> | null = null;

/**
 * Catalog SKUs are matched case-insensitively.
 *
 * The RepairShopr catalog is not internally consistent about casing — the
 * residential standard callout is stored as `RCallout.Standard` while every
 * sibling is `…CallOut.…` — so an exact-case lookup silently missed, and a
 * missed lookup is not a cosmetic failure: the line item loses its catalog
 * price and description, and is sent to RS with no `productId`, producing a
 * free-text invoice line with no product behind it.
 *
 * Resolve through these helpers, and write the *product's* `sku` back rather
 * than the caller's spelling, so the canonical casing is what leaves the app.
 */
export function normalizeSku(sku: string): string {
  return sku.trim().toLowerCase();
}

/** Build a SKU → product index suitable for `getProductFromIndex`. */
export function buildProductSkuIndex(
  products: CatalogProduct[],
): Map<string, CatalogProduct> {
  return new Map(products.map(p => [normalizeSku(p.sku), p]));
}

/** Look a SKU up in an index built by `buildProductSkuIndex`. */
export function getProductFromIndex(
  index: Map<string, CatalogProduct>,
  sku: string | undefined,
): CatalogProduct | undefined {
  if (!sku) return undefined;
  return index.get(normalizeSku(sku));
}

export function useProductCatalog() {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    setIsStale(false);

    try {
      const result = await getProducts();

      if (signal?.aborted) return;

      cachedProductMap = buildProductSkuIndex(result.items);
      setProducts(result.items);
      setIsStale(result.isStale ?? false);

      if (result.warning) {
        console.warn('[ProductCatalog]', result.warning);
      }
    } catch (err: any) {
      if (signal?.aborted) return;
      setError(err.message || 'Failed to load product catalog');
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const retry = useCallback(() => {
    invalidateProductsCache();
    cachedProductMap = null;
    load();
  }, [load]);

  return { products, isLoading, isStale, error, retry };
}

/**
 * Synchronous lookup by SKU. Returns undefined if cache not yet populated.
 * Intended for use in pricing rules where the catalog is already loaded.
 */
export function getProductBySku(sku: string): CatalogProduct | undefined {
  if (!cachedProductMap) return undefined;
  return getProductFromIndex(cachedProductMap, sku);
}
